/* eslint-disable no-restricted-globals */
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import timezones from 'timezones-list';
import { Company, Location, SiteType } from '../../stores/types';
import { isSiteTypeValid } from '../../stores/types/sitetype.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBox,
  CheckBoxData,
  ColorType,
  Dropdown,
  FormInput,
  GroupDirection,
  Icon,
  Label,
  LabelType,
  RadioGroup,
  SiteTypeDropdown,
  useGlobalModalContext,
} from '../_ui';

import { Dollar, Info, percent } from '../../assets/icons';
import { Currency } from '../../constant/Currency.constant';
import { IconSize } from '../../constant/IconSize.constant';
import { useAuth, USER_ROLE } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import {
  useAddCompanyMutation,
  useLazyCheckCompanyNameQuery,
  useUpdateCompanyMutation,
} from '../../services/company.api';
import { useFetchFeePackagesByNameQuery } from '../../services/feePackages.api';
import { useAddLocationToNewCompanyMutation } from '../../services/location.api';
import { useUpdateCurrencyMutation } from '../../services/pricing.api';
import { useFetchWhiteLabelsQuery } from '../../services/whitelabels.api';
import { BankInfoProvided, BusinessInfo } from '../../stores/types/company.interface';
import {
  FeePackage,
  FeePackageFrontendType,
  FeePaidBy,
  FeeRule,
  FeeRuleType,
  PackageType,
} from '../../stores/types/feePackage.interface';
import { formatPercent } from '../Vehicles/utils';
import { Form } from '../_ui/Form.component';
import { InputType, ValidationType } from '../_ui/Input.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { AddressAutocomplete, AddressValue } from '../_ui/address/AddressAutocomplete.component';
import {
  countryCodes,
  frontendLabelMap,
  getCountryList,
  getPhoneCountryList,
} from './companyEditor.utils';

interface CompanyEditorProp {
  company?: Company;
}

export const CompanyEditor = ({ company }: CompanyEditorProp) => {
  const auth = useAuth();

  const { t } = useTranslation();
  const { hideModal } = useGlobalModalContext();
  const { companies: companylist } = useCompany();
  const [hasBeenBlurred, setIsBlurred] = useState(false);

  // State for "Create location with same address" feature
  const [createLocationWithAddress, setCreateLocationWithAddress] = useState(false);
  const [locationData, setLocationData] = useState({
    name: '',
    latitude: '' as string | number,
    longitude: '' as string | number,
    timeZone: '',
    siteType: undefined as SiteType | undefined,
  });

  const timeZoneDropdownList = useMemo(() => {
    return timezones.map((tz) => ({
      ...tz,
      id: tz.tzCode,
      selected: tz.tzCode === locationData.timeZone,
    }));
  }, [locationData.timeZone]);

  const [triggerAddLocation] = useAddLocationToNewCompanyMutation();
  const { userScope } = useCompany();
  const [updateCompany, updateCompanyQueryResult] = useUpdateCompanyMutation();
  const [updateCountryCurrency] = useUpdateCurrencyMutation();

  const [addCompany, addCompanyQueryResult] = useAddCompanyMutation();
  const [triggerCheckCompanyName, { data: checkResult }] = useLazyCheckCompanyNameQuery();
  const doesCompanyExist = useMemo(() => {
    return checkResult && checkResult.length > 0;
  }, [checkResult]);

  useEffect(() => {
    if (addCompanyQueryResult.isSuccess && addCompanyQueryResult.data?.id) {
      const setCompanyId = async () => {
        await auth.updateCompanyId(addCompanyQueryResult.data.id);
        window.location.replace(`/?companyId=${addCompanyQueryResult.data.id}`);
      };

      setCompanyId();
    }
  }, [
    addCompanyQueryResult.isSuccess,
    addCompanyQueryResult.isError,
    addCompanyQueryResult.data,
    auth,
  ]);

  const isNew = useMemo(() => {
    return company === undefined;
  }, [company]);

  const [_company, setCompany] = useState(
    company
      ? {
          ...company,
          businessInfo: {
            ...company.businessInfo,
            channelPartnerId: company.businessInfo?.channelPartnerId,
          },
        }
      : ({
          isoCountryCode: countryCodes.CA,
          transactionFeePercentage: '0',
          transactionFeeDollar: '0',
          driverSessionFee: '0',
          whiteLabellingId: '',
          feePackageId: PackageType.NONE,
          businessInfo: {
            channelPartnerId: '',
            ela: false,
            testCompany: false,
          } as BusinessInfo,
        } as Company),
  );

  const [addressValue, setAddressValue] = useState<AddressValue>({
    street: _company.street,
    streetNumber: _company.streetNumber,
    unit: _company.unit,
    city: _company.city,
    zipOrPostalCode: _company.zipOrPostalCode,
    stateOrRegion: _company.stateOrRegion,
    latitude: '',
    longitude: '',
    timeZone: '',
    isoCountryCode: _company.isoCountryCode,
  });
  const channelPartnersDropdownList = useMemo(() => {
    return (
      companylist?.map((c) => ({
        id: c.id,
        label: c.name,
        selected:
          !!_company.businessInfo?.channelPartnerId &&
          c.id === _company.businessInfo.channelPartnerId,
      })) || []
    );
  }, [companylist, _company]);

  const [phoneCountryCode, setPhoneCountryCode] = useState<string>('1');
  const [phoneNationalNumber, setPhoneNationalNumber] = useState<string>('');
  const [addressTouched, setAddressTouched] = useState(false);
  const { data: whiteLabels } = useFetchWhiteLabelsQuery();

  useEffect(() => {
    if (_company.contactPhone) {
      const result = parsePhoneNumber(_company.contactPhone);
      let phoneCC = '1';
      let phoneNN = '';
      if (result && result?.isValid()) {
        phoneCC = result.countryCallingCode ? result.countryCallingCode : phoneCC;
        phoneNN = result.nationalNumber ? result.nationalNumber : phoneNN;
      }
      setPhoneCountryCode(phoneCC);
      setPhoneNationalNumber(phoneNN);
    }
  }, [_company.contactPhone]);

  const handleCompanyChange = (key: string, value: any) => {
    setCompany({
      ..._company,
      [key]: value,
    });
  };

  const countryList = useMemo(() => {
    return getCountryList(_company.isoCountryCode);
  }, [_company.isoCountryCode]);

  const phoneCountryList = useMemo(() => {
    return getPhoneCountryList(_company.isoCountryCode);
  }, [_company.isoCountryCode]);

  const whiteLabelList = useMemo(() => {
    if (!whiteLabels || whiteLabels.length === 0) {
      return [];
    }
    return whiteLabels.map((whiteLabel) => ({
      ...whiteLabel,
      id: whiteLabel.whiteLabellingId,
      selected:
        whiteLabel.whiteLabellingId.toLowerCase() === _company.whiteLabellingId?.toLowerCase(),
    }));
  }, [whiteLabels, _company.whiteLabellingId]);

  // Fetch all fee packages
  const { data: feePackageData } = useFetchFeePackagesByNameQuery(
    { name: '' },
    { skip: auth.role !== USER_ROLE.SUPPORT },
  );

  // Extract plan data
  const plansByType = useMemo(() => {
    const map: Partial<Record<PackageType, FeePackage>> = {};

    feePackageData?.entities?.forEach((pkg) => {
      map[pkg.type] = pkg;
    });

    return map;
  }, [feePackageData]);

  const selectedPlan = useMemo(() => {
    const planId = _company.feePackageId;

    if (!planId || planId === PackageType.NONE || planId === '') {
      return null;
    }

    return feePackageData?.entities?.find((pkg) => pkg.id === planId) || null;
  }, [_company.feePackageId, feePackageData]);

  const isFlexPlan = () => selectedPlan?.type === PackageType.FLEX_PLAN;

  // TODO: Replace with a data-driven check once the backend exposes a
  // `driverFeeEnabled` flag (or equivalent) on FeePackage. Until then, the safest
  // proxy is "every plan that is NOT Basic or Pro shows the driver session fee field",
  // because Basic and Pro are the only plans explicitly designed without a driver fee.
  const isDriverFeeEnabled = () => {
    return (
      selectedPlan?.type !== PackageType.BASIC_PLAN && selectedPlan?.type !== PackageType.PRO_PLAN
    );
  };

  const handleSaveClick = async () => {
    const payload = {
      ..._company,
      contactEmail: _company.contactEmail || '',
      hubSpotCompanyRecordId: _company.hubSpotCompanyRecordId || '',
      contactPhone: phoneNationalNumber ? `+${phoneCountryCode}${phoneNationalNumber}` : '',
      businessInfo: {
        ..._company.businessInfo,
        channelPartnerId: _company.businessInfo?.channelPartnerId,
      },
    };

    const FLEX_ID = plansByType[PackageType.FLEX_PLAN]?.id;
    const BASIC_ID = plansByType[PackageType.BASIC_PLAN]?.id;
    const PRO_ID = plansByType[PackageType.PRO_PLAN]?.id;

    const planId = _company.feePackageId;

    if (planId === FLEX_ID) {
      payload.transactionFeeDollar = '';
    }

    if (planId === BASIC_ID || planId === PRO_ID) {
      payload.driverSessionFee = '';
    }

    // For NONE plan, ensure the feePackageId is empty.
    if (!planId || planId === PackageType.NONE) {
      payload.feePackageId = '';
    }

    if (isNew) {
      const result = await addCompany(payload);

      // If checkbox is checked and company was created successfully, create location
      if (createLocationWithAddress && 'data' in result && result.data?.id) {
        const locationPayload: Partial<Location> = {
          name: locationData.name,
          latitude: Number(locationData.latitude),
          longitude: Number(locationData.longitude),
          timeZone: locationData.timeZone,
          street: _company.street || '',
          streetNumber: _company.streetNumber || '',
          unit: _company.unit || '',
          city: _company.city || '',
          zipOrPostalCode: _company.zipOrPostalCode || '',
          stateOrRegion: _company.stateOrRegion || '',
          isoCountryCode: _company.isoCountryCode || '',
          companyId: result.data.id,
          siteType: locationData.siteType,
        };
        triggerAddLocation(locationPayload as Location);
      }
    } else {
      updateCompany({ company: payload, scope: userScope! });
    }
  };

  const updateCurrency = () => {
    if (updateCompanyQueryResult.data?.currency) {
      updateCountryCurrency({
        currency: updateCompanyQueryResult.data?.currency,
      });
    }
  };

  const isPhoneNumberValid = useMemo(() => {
    return (
      phoneNationalNumber === '' ||
      isValidPhoneNumber(phoneNationalNumber, _company.isoCountryCode as countryCodes)
    );
  }, [phoneNationalNumber, _company.isoCountryCode]);

  const addressErrors = useMemo(() => {
    if (!addressTouched) return {};
    return {
      zip: !_company.zipOrPostalCode,
      state: !_company.stateOrRegion,
      city: !_company.city,
      street: !_company.street,
      streetNumber: !_company.streetNumber,
    };
  }, [
    addressTouched,
    _company.zipOrPostalCode,
    _company.stateOrRegion,
    _company.city,
    _company.street,
    _company.streetNumber,
  ]);

  const onButtonValidation = () => {
    // empty
    if (!_company.name) {
      return false;
    }

    if (
      !_company.streetNumber ||
      !_company.street ||
      !_company.city ||
      !_company.zipOrPostalCode ||
      !_company.stateOrRegion ||
      !_company.isoCountryCode
    ) {
      return false;
    }

    // validate phone number
    if (!isPhoneNumberValid) {
      return false;
    }
    // validate transaction fee percentage (required for all plans)
    if (auth.role === USER_ROLE.SUPPORT && _.isEmpty(String(_company.transactionFeePercentage))) {
      return false;
    }

    // validate transaction fee dollar (required for all plans except Flex)
    if (
      auth.role === USER_ROLE.SUPPORT &&
      !isFlexPlan() &&
      _.isEmpty(String(_company.transactionFeeDollar))
    ) {
      return false;
    }

    // validate driver session fee (required for plans except Pro & Basic)
    if (
      auth.role === USER_ROLE.SUPPORT &&
      isDriverFeeEnabled() &&
      _.isEmpty(String(_company.driverSessionFee))
    ) {
      return false;
    }

    if (auth.role === USER_ROLE.SUPPORT && !_company?.businessInfo?.channelPartnerId) {
      return false;
    }
    if (auth.role === USER_ROLE.SUPPORT && _.isEmpty(String(_company.whiteLabellingId))) {
      return false;
    }

    if (
      !isNew &&
      auth.role === USER_ROLE.SUPPORT &&
      _company.bankInfoProvided !== BankInfoProvided.MISSING &&
      _company.bankInfoProvided !== BankInfoProvided.NOT_REQUIRED &&
      _company.bankInfoProvided !== BankInfoProvided.PROVIDED
    ) {
      return false;
    }

    if (createLocationWithAddress) {
      if (
        !locationData.name ||
        !locationData.latitude ||
        !Number(locationData.latitude) ||
        Number(locationData.latitude) < -90 ||
        Number(locationData.latitude) > 90 ||
        !locationData.longitude ||
        !Number(locationData.longitude) ||
        Number(locationData.longitude) < -180 ||
        Number(locationData.longitude) > 180 ||
        !locationData.timeZone
      ) {
        return false;
      }
      if (!isSiteTypeValid(locationData.siteType)) {
        return false;
      }
    }

    // is company already exist?
    return !doesCompanyExist;
  };

  const handleCompanyNameChange = (value: string) => {
    handleCompanyChange('name', value);
  };

  const handleBusinessInfoChange = (key: string, value: any) => {
    if (key === 'channelPartnerId' && value) {
      setCompany({
        ..._company,
        businessInfo: {
          ..._company.businessInfo,
          [key]: value,
          ela: false, // set ela to false if channelPartnerId is not None
        } as BusinessInfo,
      });
    } else {
      setCompany({
        ..._company,
        businessInfo: {
          ..._company.businessInfo,
          [key]: value,
        } as BusinessInfo,
      });
    }
  };

  /**
   * Method handles the creation of Bank Info Status radio button options
   */
  const bankInfoStatusOptions = () => {
    return [
      {
        id: BankInfoProvided.PROVIDED,
        label: t('banking_info_present'),
        selected: _company.bankInfoProvided === BankInfoProvided.PROVIDED,
      },
      {
        id: BankInfoProvided.MISSING,
        label: t('banking_info_not_present'),
        selected: _company.bankInfoProvided === BankInfoProvided.MISSING,
      },
      {
        id: BankInfoProvided.NOT_REQUIRED,
        label: t('banking_info_not_required'),
        selected: _company.bankInfoProvided === BankInfoProvided.NOT_REQUIRED,
      },
    ];
  };

  const handleBankInfoStatusChange = (items: CheckBoxData[]) => {
    const selectedOption = items.find((item) => item.selected === true);
    if (selectedOption) {
      handleCompanyChange('bankInfoProvided', selectedOption.id);
    }
  };

  const isCompanyElaDisabled = () => !!_company.businessInfo?.channelPartnerId;

  const renderBusinessInfo = () => {
    if (auth.role !== USER_ROLE.SUPPORT) {
      return null;
    }

    return (
      <div className='flex flex-col gap-4 mb-1'>
        <div className='flex flex-col gap-2'>
          <Label
            text={t('company_channel_partner')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <Dropdown
            placement='top-start'
            headerWidth='100%'
            contentDivWidth={480}
            items={channelPartnersDropdownList}
            onItemClick={(item: any) => {
              handleBusinessInfoChange('channelPartnerId', item.id);
            }}
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
            dataTestId='companyChannel'
          />
        </div>
        {/* Only show following field while editing company to support users */}
        {!isNew && (
          <div className='flex flex-col gap-2'>
            <Label
              text={t('banking_info_provided_title')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
            <RadioGroup
              itemHeight='20px'
              direction={GroupDirection.Horizontal}
              defaultItems={bankInfoStatusOptions()}
              onChange={handleBankInfoStatusChange}
            />
          </div>
        )}
        <div className='flex flex-col gap-2'>
          <Label text={t('company_ela_status')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
          <CheckBox
            name='all_chargers'
            label={t('company_ela_desc')}
            translationOn
            selected={_company.businessInfo?.ela}
            onChange={(checked: boolean) => handleBusinessInfoChange('ela', checked)}
            isDisabled={isCompanyElaDisabled()}
          />
        </div>
        <div className='flex flex-col gap-2'>
          <Label text={t('company_test_status')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
          <CheckBox
            name='all_chargers'
            label={t('company_test_status_desc')}
            translationOn
            selected={_company.businessInfo?.testCompany}
            onChange={(checked: boolean) => handleBusinessInfoChange('testCompany', checked)}
          />
        </div>
      </div>
    );
  };

  const planOptions = useMemo(() => {
    const currentFeePackageId = _company.feePackageId;
    const options = [];
    const isNoneSelected =
      !currentFeePackageId ||
      currentFeePackageId === PackageType.NONE ||
      currentFeePackageId === '';

    options.push({
      id: PackageType.NONE,
      label: FeePackageFrontendType.NONE,
      selected: isNoneSelected,
      planData: {},
    });
    feePackageData?.entities?.forEach((pkg) => {
      const label = frontendLabelMap[pkg.type];
      const isSelected = currentFeePackageId === pkg.id;
      options.push({ id: pkg.id, label, selected: isSelected, planData: pkg });
    });

    return options;
  }, [feePackageData, _company.feePackageId]);

  const getCurrencyByCountryCode = (isoCountryCode: string): string => {
    switch (isoCountryCode) {
      case countryCodes.JM: // Jamaica
        return Currency.JMD;
      case countryCodes.BB: // Barbados
        return Currency.BBD;
      case countryCodes.US: // United States
        return Currency.USD;
      case countryCodes.CA: // Canada
      default:
        return Currency.CAD;
    }
  };

  const getAmountForCurrency = (
    amounts: Array<{ currency: string; value: number }> | undefined,
    targetCurrency: string,
  ): number | undefined => {
    if (!amounts || amounts.length === 0) return undefined;
    const currencyAmount = amounts.find((amount) => amount.currency === targetCurrency);
    return currencyAmount?.value;
  };

  const extractPlanDefaults = (plan: any, prev: Company) => {
    if (!plan) {
      return {
        transactionFeeDollar: prev.transactionFeeDollar,
        transactionFeePercentage: prev.transactionFeePercentage,
        driverSessionFee: prev.driverSessionFee,
      };
    }

    const flatRateFeeRulePaidBySitehost = plan.feeRules?.find(
      (r: FeeRule) => r.type === FeeRuleType.FLAT_RATE && r.paidBy === FeePaidBy.SITE_HOST,
    );

    const percentageFeeRule = plan.feeRules?.find(
      (r: FeeRule) => r.type === FeeRuleType.PERCENTAGE && r.paidBy === FeePaidBy.SITE_HOST,
    );

    const flatRateFeeRulePaidByDriver = plan.feeRules?.find(
      (r: FeeRule) => r.type === FeeRuleType.FLAT_RATE && r.paidBy === FeePaidBy.DRIVER,
    );

    const targetCurrency = getCurrencyByCountryCode(_company.isoCountryCode || countryCodes.CA);

    return {
      transactionFeeDollar:
        getAmountForCurrency(flatRateFeeRulePaidBySitehost?.amounts, targetCurrency)?.toString() ??
        prev.transactionFeeDollar,

      transactionFeePercentage:
        percentageFeeRule?.percentage?.toString() ?? prev.transactionFeePercentage,

      driverSessionFee:
        getAmountForCurrency(flatRateFeeRulePaidByDriver?.amounts, targetCurrency)?.toString() ??
        prev.driverSessionFee,
    };
  };

  const updateFeeAmountsForCountry = (isoCountryCode: string) => {
    if (!selectedPlan || !selectedPlan.feeRules) return;

    const validTypes = [PackageType.FLEX_PLAN, PackageType.BASIC_PLAN, PackageType.PRO_PLAN];
    if (!validTypes.includes(selectedPlan.type)) return;
    const newCurrency = getCurrencyByCountryCode(isoCountryCode);
    const flat = selectedPlan.feeRules.find(
      (r: FeeRule) => r.type === FeeRuleType.FLAT_RATE && r.paidBy === FeePaidBy.SITE_HOST,
    );
    const driver = selectedPlan.feeRules.find(
      (r: FeeRule) => r.type === FeeRuleType.FLAT_RATE && r.paidBy === FeePaidBy.DRIVER,
    );

    setCompany((prev) => ({
      ...prev,
      transactionFeeDollar:
        getAmountForCurrency(flat?.amounts, newCurrency)?.toString() ?? prev.transactionFeeDollar,
      driverSessionFee:
        getAmountForCurrency(driver?.amounts, newCurrency)?.toString() ?? prev.driverSessionFee,
    }));
  };

  const handleCountryChange = (item: any) => {
    handleCompanyChange('isoCountryCode', item.isoCode);
    setPhoneCountryCode(item.phonecode);
    setPhoneNationalNumber('');
    updateFeeAmountsForCountry(item.isoCode);
  };

  const handlePlanSelection = (item: { id: string }) => {
    const newId = item.id === PackageType.NONE ? '' : item.id;

    const isNoDefaultsPlan =
      newId === '' ||
      newId === plansByType[PackageType.ELA]?.id ||
      newId === plansByType[PackageType.ESSENTIALS]?.id;

    const planData = [
      plansByType[PackageType.FLEX_PLAN],
      plansByType[PackageType.BASIC_PLAN],
      plansByType[PackageType.PRO_PLAN],
      plansByType[PackageType.ESSENTIALS_PLUS],
    ].find((p) => p?.id === newId);

    setCompany((prev) => {
      if (isNoDefaultsPlan) {
        return {
          ...prev,
          feePackageId: newId,
          transactionFeeDollar: '0',
          transactionFeePercentage: '0',
          driverSessionFee: '0',
        };
      }

      if (!planData) {
        return { ...prev, feePackageId: newId };
      }

      return {
        ...prev,
        feePackageId: newId,
        ...extractPlanDefaults(planData, prev),
      };
    });
  };

  return (
    <Form
      onSubmit={handleSaveClick}
      queryResult={isNew ? addCompanyQueryResult : updateCompanyQueryResult}
      onQuerySuccess={() => {
        hideModal();
        if (!isNew) {
          updateCurrency();
        }
      }}
      onQueryFailed={() => {
        hideModal();
      }}
    >
      <div className='flex flex-col justify-between h-full'>
        <div className='flex flex-col gap-4'>
          <FormInput
            inputId='company_name'
            placeholder={t('company_name')}
            label={t('company_name')}
            onChange={(event) => handleCompanyNameChange(event.target.value)}
            onBlur={() => {
              if (_company.name?.length > 0) {
                triggerCheckCompanyName(_company.name);
              }
            }}
            validationType={ValidationType.NOT_EMPTY}
            defaultValue={_company.name}
            width='100%'
            errorLabel={
              _company.name?.length > 0 ? t('company_name_duplicated') : t('company_name_required')
            }
            dataTestId='companyNameInput'
          />
          <AddressAutocomplete
            value={addressValue}
            onChange={(next) => {
              setAddressTouched(true);
              const updated = { ...addressValue, ...next };
              setAddressValue(updated);

              // Update company state
              setCompany((prev) => ({
                ...prev,
                street: updated.street,
                streetNumber: updated.streetNumber,
                unit: updated.unit,
                city: updated.city,
                zipOrPostalCode: updated.zipOrPostalCode,
                stateOrRegion: updated.stateOrRegion,
                // We Do not update isoCountryCode here
                // Country is managed exclusively via onCountryChange={handleCountryChange}
              }));

              // Auto-update location name and geo data if checkbox is checked
              if (createLocationWithAddress && updated.streetNumber && updated.street) {
                setLocationData((prev) => ({
                  ...prev,
                  name: `${updated.streetNumber} ${updated.street}`,
                  latitude: updated.latitude ?? prev.latitude,
                  longitude: updated.longitude ?? prev.longitude,
                  timeZone: updated.timeZone || prev.timeZone,
                }));
              }
            }}
            countryIsoCode={_company.isoCountryCode || 'CA'}
            labels={{
              addressLabel: t('company_address'),
              streetNumber: t('company_edit_street_number_holder'),
              streetName: t('company_edit_street_name_holder'),
              city: t('company_city'),
              state: t('company_state'),
              zip: t('company_zip'),
            }}
            include={{
              unit: true,
              state: true,
              country: true,
              timezone: false,
              geo: false,
            }}
            errors={addressErrors}
            errorLabels={{
              zip: t('company_zip_required'),
              city: t('company_city_required'),
              state: t('company_state_required'),
              street: t('company_street_required'),
              streetNumber: t('company_street_number_required'),
            }}
            stateRequired={createLocationWithAddress}
            showStateOptionalHint={false}
            countryDropdownItems={countryList}
            onCountryChange={handleCountryChange}
          />
          {/* Create Location with Same Address - Only show for new companies */}
          {isNew && (
            <div className='flex flex-col gap-4'>
              <CheckBox
                name='create_location_with_address'
                label={t('create_location_with_same_address')}
                translationOn
                selected={createLocationWithAddress}
                onChange={(checked: boolean) => {
                  setCreateLocationWithAddress(checked);
                  if (checked && addressValue.streetNumber && addressValue.street) {
                    setLocationData({
                      name: `${addressValue.streetNumber} ${addressValue.street}`,
                      latitude: addressValue.latitude ?? '',
                      longitude: addressValue.longitude ?? '',
                      timeZone: addressValue.timeZone || '',
                      siteType: undefined,
                    });
                  }
                }}
              />

              {createLocationWithAddress && (
                <div className='flex flex-col gap-4 pl-6 border-l-2 border-grey3'>
                  {/* Location Name */}
                  <FormInput
                    inputId='location_name'
                    label={t('location_name')}
                    placeholder={t('location_name')}
                    onChange={(event) =>
                      setLocationData((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    validationType={ValidationType.NOT_EMPTY}
                    defaultValue={locationData.name}
                    width='100%'
                    dataTestId='locationNameInput'
                  />

                  <SiteTypeDropdown
                    value={locationData.siteType}
                    onChange={(siteType: SiteType | undefined) =>
                      setLocationData((prev) => ({ ...prev, siteType }))
                    }
                    dataTestId='companySiteType'
                  />

                  {/* Geolocation */}
                  <div className='flex flex-col gap-2'>
                    <div className='flex flex-row gap-2'>
                      <Label
                        text={t('location_geo')}
                        type={LabelType.LABEL_M}
                        color={ColorType.BLACK}
                      />
                      <Tooltip>
                        <TooltipTrigger>
                          <Icon src={Info} size={IconSize.SIZE_20x20} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <span className='text-white font-medium text-sm'>
                            <Trans i18nKey='location_geolocaiton_tip' components={{ br: <br /> }} />
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className='flex flex-row gap-2'>
                      <div className='flex-1 flex-col'>
                        <Label
                          text={t('location_lat')}
                          type={LabelType.BODY3}
                          color={ColorType.BLACK}
                        />
                        <FormInput
                          inputId='location_lat'
                          placeholder={t('location_lat_placeholder')}
                          onChange={(event) =>
                            setLocationData((prev) => ({
                              ...prev,
                              latitude: event.target.value,
                            }))
                          }
                          defaultValue={locationData.latitude}
                          inputType={InputType.GEOLOCATION}
                          validationType={ValidationType.GEO_LAT}
                          errorLabel={t('location_wrong_lat')}
                          dataTestId='locationLatInput'
                          width='100%'
                        />
                      </div>
                      <div className='flex-1 flex-col'>
                        <Label
                          text={t('location_lon')}
                          type={LabelType.BODY3}
                          color={ColorType.BLACK}
                        />
                        <FormInput
                          inputId='location_lon'
                          placeholder={t('location_lon_placeholder')}
                          onChange={(event) =>
                            setLocationData((prev) => ({
                              ...prev,
                              longitude: event.target.value,
                            }))
                          }
                          defaultValue={locationData.longitude}
                          inputType={InputType.GEOLOCATION}
                          validationType={ValidationType.GEO_LON}
                          errorLabel={t('location_wrong_lon')}
                          dataTestId='locationLonInput'
                          width='100%'
                        />
                      </div>
                    </div>
                    <Label
                      className='pt-1'
                      text={t('check_geolocation')}
                      type={LabelType.LABEL_S_MEDIUM}
                      color={ColorType.BRAND2}
                      onClick={() => {
                        window.open(
                          `https://maps.google.com/maps/search/?api=1&query=${locationData.latitude},${locationData.longitude}`,
                          '_clmapcheck',
                        );
                      }}
                    />
                  </div>

                  {/* Timezone */}
                  <div className='flex flex-col gap-2'>
                    <Label
                      text={t('location_timezone')}
                      type={LabelType.LABEL_M}
                      color={ColorType.BLACK}
                    />
                    <Dropdown
                      placeholder={t('location_timezone_placeholder')}
                      headerWidth='100%'
                      contentDivWidth={480}
                      items={timeZoneDropdownList}
                      onItemClick={(item: any) =>
                        setLocationData((prev) => ({
                          ...prev,
                          timeZone: item.tzCode,
                        }))
                      }
                      labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                      chevdownIconHighlightColor={ColorType.GREY6}
                      headerHighLightClassName='hover:bg-grey2 bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                      contentDivHeight={300}
                      dataTestId='locationTimezoneDropdown'
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2 items-baseline'>
              <Label
                text={t('company_phone_number')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                labelHtmlFor='company_phone_number'
              />
              <Label text={`(${t('optional')})`} type={LabelType.BODY3} color={ColorType.GREY6} />
            </div>
            <div className='flex flex-row gap-2'>
              <div style={{ width: '30%' }}>
                <Dropdown
                  headerWidth='100%'
                  label='flagAndCode'
                  items={phoneCountryList}
                  onItemClick={(item: any) => {
                    setPhoneCountryCode(item.phonecode);
                    setPhoneNationalNumber('');
                  }}
                  labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                  chevdownIconHighlightColor={ColorType.GREY6}
                  headerHighLightClassName='hover:bg-grey2 bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                  contentDivHeight={200}
                />
              </div>
              <div style={{ width: '70%' }}>
                <FormInput
                  inputId='company_phone_number'
                  placeholder={t('placeholder_phone')}
                  onChange={(event) => {
                    setPhoneNationalNumber(event.target.value);
                  }}
                  validationType={ValidationType.NUMBER}
                  defaultValue={phoneNationalNumber}
                  width='100%'
                  inputType={InputType.NUMBER}
                  maxLen='16'
                  validationQueryResult={hasBeenBlurred && !isPhoneNumberValid}
                  errorLabel={t('phone_number_invalid')}
                  dataTestId='companyPhoneInput'
                  onBlur={() => setIsBlurred(true)}
                />
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2 items-baseline'>
              <Label
                text={t('company_email')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                labelHtmlFor='company_email'
              />
              <Label text={`(${t('optional')})`} type={LabelType.BODY3} color={ColorType.GREY6} />
            </div>
            <FormInput
              inputId='company_email'
              placeholder={t('company_email_holder')}
              showErrorIcon
              onChange={(event) => handleCompanyChange('contactEmail', event.target.value)}
              validationType={ValidationType.EMAIL}
              defaultValue={_company.contactEmail}
              width='100%'
              dataTestId='companyEmail'
            />
          </div>
          {auth.role === USER_ROLE.SUPPORT && (
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <Label text={t('tax_id')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
              </div>
              <div>
                <Label
                  text={company?.companyTaxId || t('not_yet_entered_in_hubspot')}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  skeletonHeight='h-5'
                />
              </div>
            </div>
          )}
          {auth.role === USER_ROLE.SUPPORT && (
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <Label
                  text={t('hubspot_company_record_id')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
                <Label text={`(${t('optional')})`} type={LabelType.BODY3} color={ColorType.GREY6} />
              </div>
              <FormInput
                inputId='hubspot_company_record_id'
                placeholder={t('hubspot_company_record_id')}
                width='100%'
                onChange={(event) =>
                  handleCompanyChange('hubSpotCompanyRecordId', event.target.value)
                }
                defaultValue={_company.hubSpotCompanyRecordId || ''}
                dataTestId='hubspotCompanyRecordIdInput'
                inputType={InputType.POSITIVE_NUMBER}
              />
            </div>
          )}

          {auth.role === USER_ROLE.SUPPORT && (
            <div className='flex flex-col gap-2'>
              <Label
                text={t('plan')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                className='mb-2'
              />
              <Dropdown
                placement='bottom-start'
                headerWidth='100%'
                contentDivWidth={480}
                items={planOptions}
                placeholder={
                  planOptions.find((option) => option.selected)?.label || t('select_a_plan')
                }
                onItemClick={handlePlanSelection}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                dataTestId='companyPlanDropdown'
              />
            </div>
          )}
          {auth.role === USER_ROLE.SUPPORT && (
            <div>
              <Label
                text={t('company_service_fee')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                className='mb-2'
              />
              <div className='flex items-center gap-2'>
                <FormInput
                  inputId='company_transaction_fee_percent'
                  showErrorIcon
                  onChange={(event) =>
                    handleCompanyChange(
                      'transactionFeePercentage',
                      formatPercent(event.target.value),
                    )
                  }
                  inputType={InputType.PERCENT}
                  defaultValue={_company.transactionFeePercentage ?? '0'}
                  suffixIcon={percent}
                  suffixIconSize={IconSize.SIZE_14x14}
                  width='93px'
                  validationType={[ValidationType.NOT_EMPTY, ValidationType.PERCENT]}
                />
                {!isFlexPlan() && (
                  <>
                    <Label text={t('plus')} type={LabelType.BODY3} color={ColorType.BLACK} />
                    <FormInput
                      inputId='company_transaction_fee_dollar'
                      showErrorIcon
                      onChange={(event) =>
                        handleCompanyChange(
                          'transactionFeeDollar',
                          formatPercent(event.target.value),
                        )
                      }
                      inputType={InputType.CURRENCY_INPUT}
                      validationType={[
                        ValidationType.NOT_EMPTY,
                        ValidationType.CURRENCY_ZERO_ALLOWED,
                      ]}
                      defaultValue={_company.transactionFeeDollar ?? '0'}
                      companyCurrency={_company?.currency}
                      icon={Dollar}
                      width='93px'
                    />
                  </>
                )}
                <Label text={t('per_transaction')} type={LabelType.BODY3} color={ColorType.BLACK} />
              </div>
            </div>
          )}
          {auth.role === USER_ROLE.SUPPORT && isDriverFeeEnabled() && (
            <div>
              <Label
                text={t('driver_session_fee_dollar')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                className='mb-2'
              />
              <div className='flex items-center gap-2'>
                <FormInput
                  inputId='company_driver_session_fee'
                  showErrorIcon
                  onChange={(event) =>
                    handleCompanyChange('driverSessionFee', formatPercent(event.target.value))
                  }
                  inputType={InputType.CURRENCY_INPUT}
                  validationType={[ValidationType.NOT_EMPTY, ValidationType.CURRENCY_ZERO_ALLOWED]}
                  defaultValue={_company.driverSessionFee ?? '0'}
                  companyCurrency={_company?.currency}
                  icon={Dollar}
                  width='93px'
                />
                <Label text={t('per_transaction')} type={LabelType.BODY3} color={ColorType.BLACK} />
              </div>
            </div>
          )}
          {auth.role === USER_ROLE.SUPPORT && (
            <div className='flex flex-col gap-2'>
              <Label text={t('company_network')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
              <Dropdown
                placement='top-start'
                headerWidth='100%'
                contentDivWidth={480}
                label='whiteLabellingId'
                items={whiteLabelList}
                onItemClick={(item: any) => {
                  handleCompanyChange('whiteLabellingId', item.whiteLabellingId);
                }}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                dataTestId='companyNetwork'
              />
            </div>
          )}
          {renderBusinessInfo()}
        </div>
        <div className='sticky bottom-0 pt-[16px] pb-[20px] bg-white'>
          <Button
            label={t('save')}
            type={ButtonType.PRIMARY}
            disabled={!onButtonValidation()}
            size={ButtonSize.SMALL_FULL}
            isSumbit
            dataTestId='saveCompanyButton'
          />
        </div>
      </div>
    </Form>
  );
};
